/* eslint-disable */
import {
  Payment_AcquiringBank,
  payment_AcquiringBankFromJSON,
  payment_AcquiringBankToJSON,
  Receipt_DonationUnit,
  SalesOrder_PaymentType,
  salesOrder_PaymentTypeFromJSON,
  salesOrder_PaymentTypeToJSON,
  SalesOrder_ReceiptMethod,
  salesOrder_ReceiptMethodFromJSON,
  salesOrder_ReceiptMethodToJSON,
} from "../../../api/orders/resources";
import { FieldMask } from "../../../google/protobuf/field_mask";
import {
  Announcement,
  PaymentShippingInfo,
  PaymentShippingOverseaInfo,
  ProductMeta,
  RecipientAddress,
} from "../common/common";

export const protobufPackage = "store.api.store";

export interface UpdateCartRequest {
  /** Optional. Can be empty. */
  items: Cart_Item[];
}

export interface GetCartInfoRequest {
  /** Required. */
  items: Cart_Item[];
}

export interface MergeCartRequest {
  /**
   * Required. If false, then filter out all unpublished cart item skus and add the rest to cart.
   * If true, then return error when any of the cart item sku is unpublished. Error message will
   * differ for the following cases:
   *   - 1 item and unpublished
   *   - n items and all unpublished
   *   - n items and less than n unpublished
   */
  strict: boolean;
  /** Optional. Can be empty. */
  items: Cart_Item[];
}

export interface AddCartItemRequest {
  /** Required. */
  item:
    | Cart_Item
    | undefined;
  /** Optional. If specified and found, then the add `Cart.Item` will be inserted in this replaced index. */
  replaceSkuId: string;
}

export interface GetShippingInfosRequest {
  /**
   * Optional. 要結帳的購物車商品清單 Use only items with selected = true.
   * Required for CartInfo.
   */
  items: Cart_Item[];
  /** Optional. */
  mask: string[] | undefined;
}

export interface GetShippingInfosResponse {
  /** 付款與運送方式 */
  shippingInfo:
    | GetShippingInfosResponse_ShippingInfo
    | undefined;
  /** 結算金額資訊 */
  cartInfo:
    | GetShippingInfosResponse_CartInfo
    | undefined;
  /** 常用收件資訊 */
  recipientInfo:
    | GetShippingInfosResponse_RecipientInfo
    | undefined;
  /** 發票處理資訊 */
  receiptInfo: GetShippingInfosResponse_ReceiptInfo | undefined;
}

/** 付款與運送方式 */
export interface GetShippingInfosResponse_ShippingInfo {
  /**
   * 付款與運送方式
   * -. 只會回傳該使用者可使用的下單方式
   * -. 如果其中的 over_limit_remind 有文案代表此項目要灰底, 使用者點選後要出 alert
   */
  paymentShippingInfos: PaymentShippingInfo[];
  /** 海外配送資訊 */
  paymentShippingOverseaInfos: PaymentShippingOverseaInfo[];
  /** 上次選擇的付款與運送方式 */
  lastSelectedType: SalesOrder_PaymentType;
  /** 上次選擇的海外地區, for last_selected_type = PAYMENT_TYPE_OVERSEA */
  lastOverseaName: string;
  /** 美金兌換台幣匯率 */
  exchangeRateUsdNtd: string;
  /** 信用卡收單銀行 */
  acquiringBank: Payment_AcquiringBank;
}

/** 結算金額資訊 */
export interface GetShippingInfosResponse_CartInfo {
  /** 商品原價 x 件數 加總 */
  originPriceTotal: string;
  /** (商品原價 - 活動價) x 件數 加總 */
  promotionDeduction: string;
  /** 帳戶餘額 */
  balance: string;
}

/** 常用收件資訊 */
export interface GetShippingInfosResponse_RecipientInfo {
  /** 常用收件資訊 */
  recipientInfos: RecipientAddress[];
  /**
   * 外島轉郵文案
   * 當 PaymentType 是 PAYMENT_TYPE_CREDIT_CARD/PAYMENT_TYPE_LINE_PAY/PAYMENT_TYPE_APPLE_PAY,
   * 如果選擇的收件人地址是離島, 要出文案
   */
  islandPostOfficeNote: string;
  /**
   * COD 僅限本島文案
   * 當 PaymentType 是 PAYMENT_TYPE_CASH_ON_DELIVERY,
   * 如果選擇的收件人地址是離島, 要出文案
   */
  islandCodNote: string;
}

/** 發票處理資訊 */
export interface GetShippingInfosResponse_ReceiptInfo {
  /** 最近一次的 發票處理方式 */
  receiptMethod: SalesOrder_ReceiptMethod;
  /** 最近一次的 手機條碼 */
  mobileBarcode: string;
  /** 最近一次的 統一編號 */
  taxId: string;
  /** 最近一次的 捐贈碼 */
  donateUnitId: string;
  /** 捐贈單位清單 */
  donations: Receipt_DonationUnit[];
}

export interface VerifyReceiptBarcodeRequest {
  /** Required. 手機條碼 */
  barcode: string;
}

export interface Cart {
  /** Items in this cart. */
  items: Cart_Item[];
  /** All promotions associated with the products in this cart. */
  promotions: Cart_PromotionInfo[];
  /** TYPE_BANNER announcement. Valid for MOBILE only. */
  announcement:
    | Announcement
    | undefined;
  /** 帳戶餘額 */
  balance: string;
  /** 禁止海外銷售 的文案 */
  remindProhibitOverseaSale: string;
}

/** 品項 */
export interface Cart_Item {
  /** Required. */
  skuId: string;
  /** Required. */
  quantity: number;
  /** Required. */
  selected: boolean;
  /** Output only. */
  meta:
    | ProductMeta
    | undefined;
  /** Output only. */
  published: boolean;
  /** Output only. Image 360 pixels. */
  image: string;
  /** Output only. */
  colorName: string;
  /** Output only. */
  sizeName: string;
  /** Output only. */
  promotionId: string;
  /** Output only. */
  inventory: number;
  /** Output only. */
  boughtBefore: boolean;
  /** Output only. */
  prohibitOverseaSale: boolean;
  /** Output only. */
  sizeSuffix: string;
}

/** 活動資訊 */
export interface Cart_PromotionInfo {
  /** Promotion ID */
  id: string;
  /** Promotion name */
  name: string;
  /** Promotion exist in `SalesEvent` */
  enabled: boolean;
  /** 折扣方式 */
  discountType: Cart_PromotionInfo_DiscountType;
  /** Discount */
  discount: Cart_PromotionInfo_Discount | undefined;
}

/** 折扣方式 */
export enum Cart_PromotionInfo_DiscountType {
  DISCOUNT_TYPE_UNSPECIFIED = 0,
  /** DISCOUNT_TYPE_PICKS_WITH_PRICE - 任選 n 件 x 元 */
  DISCOUNT_TYPE_PICKS_WITH_PRICE = 1,
  /** DISCOUNT_TYPE_PICKS_WITH_DISCOUNT - 任選 n 件 x 折 */
  DISCOUNT_TYPE_PICKS_WITH_DISCOUNT = 2,
  /** DISCOUNT_TYPE_PICKS_WITH_CUSTOMIZE_PRICE - 任選 n 件，自訂活動價 */
  DISCOUNT_TYPE_PICKS_WITH_CUSTOMIZE_PRICE = 3,
  /** DISCOUNT_TYPE_GIFT_WITH_PURCHASE - 滿額 n 元，贈送商品 */
  DISCOUNT_TYPE_GIFT_WITH_PURCHASE = 4,
  /** DISCOUNT_TYPE_APP_LIMIT - app限定，自訂活動價 */
  DISCOUNT_TYPE_APP_LIMIT = 5,
}

export function cart_PromotionInfo_DiscountTypeFromJSON(object: any): Cart_PromotionInfo_DiscountType {
  switch (object) {
    case 0:
    case "DISCOUNT_TYPE_UNSPECIFIED":
      return Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_UNSPECIFIED;
    case 1:
    case "DISCOUNT_TYPE_PICKS_WITH_PRICE":
      return Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_PICKS_WITH_PRICE;
    case 2:
    case "DISCOUNT_TYPE_PICKS_WITH_DISCOUNT":
      return Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_PICKS_WITH_DISCOUNT;
    case 3:
    case "DISCOUNT_TYPE_PICKS_WITH_CUSTOMIZE_PRICE":
      return Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_PICKS_WITH_CUSTOMIZE_PRICE;
    case 4:
    case "DISCOUNT_TYPE_GIFT_WITH_PURCHASE":
      return Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_GIFT_WITH_PURCHASE;
    case 5:
    case "DISCOUNT_TYPE_APP_LIMIT":
      return Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_APP_LIMIT;
    default:
      throw new tsProtoGlobalThis.Error(
        "Unrecognized enum value " + object + " for enum Cart_PromotionInfo_DiscountType",
      );
  }
}

export function cart_PromotionInfo_DiscountTypeToJSON(object: Cart_PromotionInfo_DiscountType): string {
  switch (object) {
    case Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_UNSPECIFIED:
      return "DISCOUNT_TYPE_UNSPECIFIED";
    case Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_PICKS_WITH_PRICE:
      return "DISCOUNT_TYPE_PICKS_WITH_PRICE";
    case Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_PICKS_WITH_DISCOUNT:
      return "DISCOUNT_TYPE_PICKS_WITH_DISCOUNT";
    case Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_PICKS_WITH_CUSTOMIZE_PRICE:
      return "DISCOUNT_TYPE_PICKS_WITH_CUSTOMIZE_PRICE";
    case Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_GIFT_WITH_PURCHASE:
      return "DISCOUNT_TYPE_GIFT_WITH_PURCHASE";
    case Cart_PromotionInfo_DiscountType.DISCOUNT_TYPE_APP_LIMIT:
      return "DISCOUNT_TYPE_APP_LIMIT";
    default:
      throw new tsProtoGlobalThis.Error(
        "Unrecognized enum value " + object + " for enum Cart_PromotionInfo_DiscountType",
      );
  }
}

export interface Cart_PromotionInfo_Discount {
  /**
   * `PICKS_WITH_PRICE` (任選 item_num 件 x 元)
   * `PICKS_WITH_DISCOUNT` (任選 item_num 件 x 折)
   * `PICKS_WITH_CUSTOMIZE_PRICE` (任選 item_num 件，自訂活動價)
   */
  itemNum?:
    | number
    | undefined;
  /** `GIFT_WITH_PURCHASE` (滿額 price 元，贈送商品 product_id) */
  gift?: Cart_PromotionInfo_Discount_Gift | undefined;
}

/** 滿額 n 元，贈送商品, TODO: 尚未設計 */
export interface Cart_PromotionInfo_Discount_Gift {
  /** Output only. 金額門檻 */
  price: string;
  /** Output only. 贈送之商品編號 */
  productId: string;
}

function createBaseUpdateCartRequest(): UpdateCartRequest {
  return { items: [] };
}

export const UpdateCartRequest = {
  fromJSON(object: any): UpdateCartRequest {
    return { items: Array.isArray(object?.items) ? object.items.map((e: any) => Cart_Item.fromJSON(e)) : [] };
  },

  toJSON(message: UpdateCartRequest): unknown {
    const obj: any = {};
    if (message.items) {
      obj.items = message.items.map((e) => e ? Cart_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UpdateCartRequest>, I>>(base?: I): UpdateCartRequest {
    return UpdateCartRequest.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<UpdateCartRequest>, I>>(object: I): UpdateCartRequest {
    const message = createBaseUpdateCartRequest();
    message.items = object.items?.map((e) => Cart_Item.fromPartial(e)) || [];
    return message;
  },
};

function createBaseGetCartInfoRequest(): GetCartInfoRequest {
  return { items: [] };
}

export const GetCartInfoRequest = {
  fromJSON(object: any): GetCartInfoRequest {
    return { items: Array.isArray(object?.items) ? object.items.map((e: any) => Cart_Item.fromJSON(e)) : [] };
  },

  toJSON(message: GetCartInfoRequest): unknown {
    const obj: any = {};
    if (message.items) {
      obj.items = message.items.map((e) => e ? Cart_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetCartInfoRequest>, I>>(base?: I): GetCartInfoRequest {
    return GetCartInfoRequest.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetCartInfoRequest>, I>>(object: I): GetCartInfoRequest {
    const message = createBaseGetCartInfoRequest();
    message.items = object.items?.map((e) => Cart_Item.fromPartial(e)) || [];
    return message;
  },
};

function createBaseMergeCartRequest(): MergeCartRequest {
  return { strict: false, items: [] };
}

export const MergeCartRequest = {
  fromJSON(object: any): MergeCartRequest {
    return {
      strict: isSet(object.strict) ? Boolean(object.strict) : false,
      items: Array.isArray(object?.items) ? object.items.map((e: any) => Cart_Item.fromJSON(e)) : [],
    };
  },

  toJSON(message: MergeCartRequest): unknown {
    const obj: any = {};
    message.strict !== undefined && (obj.strict = message.strict);
    if (message.items) {
      obj.items = message.items.map((e) => e ? Cart_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<MergeCartRequest>, I>>(base?: I): MergeCartRequest {
    return MergeCartRequest.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<MergeCartRequest>, I>>(object: I): MergeCartRequest {
    const message = createBaseMergeCartRequest();
    message.strict = object.strict ?? false;
    message.items = object.items?.map((e) => Cart_Item.fromPartial(e)) || [];
    return message;
  },
};

function createBaseAddCartItemRequest(): AddCartItemRequest {
  return { item: undefined, replaceSkuId: "" };
}

export const AddCartItemRequest = {
  fromJSON(object: any): AddCartItemRequest {
    return {
      item: isSet(object.item) ? Cart_Item.fromJSON(object.item) : undefined,
      replaceSkuId: isSet(object.replaceSkuId) ? String(object.replaceSkuId) : "",
    };
  },

  toJSON(message: AddCartItemRequest): unknown {
    const obj: any = {};
    message.item !== undefined && (obj.item = message.item ? Cart_Item.toJSON(message.item) : undefined);
    message.replaceSkuId !== undefined && (obj.replaceSkuId = message.replaceSkuId);
    return obj;
  },

  create<I extends Exact<DeepPartial<AddCartItemRequest>, I>>(base?: I): AddCartItemRequest {
    return AddCartItemRequest.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<AddCartItemRequest>, I>>(object: I): AddCartItemRequest {
    const message = createBaseAddCartItemRequest();
    message.item = (object.item !== undefined && object.item !== null) ? Cart_Item.fromPartial(object.item) : undefined;
    message.replaceSkuId = object.replaceSkuId ?? "";
    return message;
  },
};

function createBaseGetShippingInfosRequest(): GetShippingInfosRequest {
  return { items: [], mask: undefined };
}

export const GetShippingInfosRequest = {
  fromJSON(object: any): GetShippingInfosRequest {
    return {
      items: Array.isArray(object?.items) ? object.items.map((e: any) => Cart_Item.fromJSON(e)) : [],
      mask: isSet(object.mask) ? FieldMask.unwrap(FieldMask.fromJSON(object.mask)) : undefined,
    };
  },

  toJSON(message: GetShippingInfosRequest): unknown {
    const obj: any = {};
    if (message.items) {
      obj.items = message.items.map((e) => e ? Cart_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    message.mask !== undefined && (obj.mask = FieldMask.toJSON(FieldMask.wrap(message.mask)));
    return obj;
  },

  create<I extends Exact<DeepPartial<GetShippingInfosRequest>, I>>(base?: I): GetShippingInfosRequest {
    return GetShippingInfosRequest.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetShippingInfosRequest>, I>>(object: I): GetShippingInfosRequest {
    const message = createBaseGetShippingInfosRequest();
    message.items = object.items?.map((e) => Cart_Item.fromPartial(e)) || [];
    message.mask = object.mask ?? undefined;
    return message;
  },
};

function createBaseGetShippingInfosResponse(): GetShippingInfosResponse {
  return { shippingInfo: undefined, cartInfo: undefined, recipientInfo: undefined, receiptInfo: undefined };
}

export const GetShippingInfosResponse = {
  fromJSON(object: any): GetShippingInfosResponse {
    return {
      shippingInfo: isSet(object.shippingInfo)
        ? GetShippingInfosResponse_ShippingInfo.fromJSON(object.shippingInfo)
        : undefined,
      cartInfo: isSet(object.cartInfo) ? GetShippingInfosResponse_CartInfo.fromJSON(object.cartInfo) : undefined,
      recipientInfo: isSet(object.recipientInfo)
        ? GetShippingInfosResponse_RecipientInfo.fromJSON(object.recipientInfo)
        : undefined,
      receiptInfo: isSet(object.receiptInfo)
        ? GetShippingInfosResponse_ReceiptInfo.fromJSON(object.receiptInfo)
        : undefined,
    };
  },

  toJSON(message: GetShippingInfosResponse): unknown {
    const obj: any = {};
    message.shippingInfo !== undefined && (obj.shippingInfo = message.shippingInfo
      ? GetShippingInfosResponse_ShippingInfo.toJSON(message.shippingInfo)
      : undefined);
    message.cartInfo !== undefined &&
      (obj.cartInfo = message.cartInfo ? GetShippingInfosResponse_CartInfo.toJSON(message.cartInfo) : undefined);
    message.recipientInfo !== undefined && (obj.recipientInfo = message.recipientInfo
      ? GetShippingInfosResponse_RecipientInfo.toJSON(message.recipientInfo)
      : undefined);
    message.receiptInfo !== undefined && (obj.receiptInfo = message.receiptInfo
      ? GetShippingInfosResponse_ReceiptInfo.toJSON(message.receiptInfo)
      : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<GetShippingInfosResponse>, I>>(base?: I): GetShippingInfosResponse {
    return GetShippingInfosResponse.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetShippingInfosResponse>, I>>(object: I): GetShippingInfosResponse {
    const message = createBaseGetShippingInfosResponse();
    message.shippingInfo = (object.shippingInfo !== undefined && object.shippingInfo !== null)
      ? GetShippingInfosResponse_ShippingInfo.fromPartial(object.shippingInfo)
      : undefined;
    message.cartInfo = (object.cartInfo !== undefined && object.cartInfo !== null)
      ? GetShippingInfosResponse_CartInfo.fromPartial(object.cartInfo)
      : undefined;
    message.recipientInfo = (object.recipientInfo !== undefined && object.recipientInfo !== null)
      ? GetShippingInfosResponse_RecipientInfo.fromPartial(object.recipientInfo)
      : undefined;
    message.receiptInfo = (object.receiptInfo !== undefined && object.receiptInfo !== null)
      ? GetShippingInfosResponse_ReceiptInfo.fromPartial(object.receiptInfo)
      : undefined;
    return message;
  },
};

function createBaseGetShippingInfosResponse_ShippingInfo(): GetShippingInfosResponse_ShippingInfo {
  return {
    paymentShippingInfos: [],
    paymentShippingOverseaInfos: [],
    lastSelectedType: 0,
    lastOverseaName: "",
    exchangeRateUsdNtd: "",
    acquiringBank: 0,
  };
}

export const GetShippingInfosResponse_ShippingInfo = {
  fromJSON(object: any): GetShippingInfosResponse_ShippingInfo {
    return {
      paymentShippingInfos: Array.isArray(object?.paymentShippingInfos)
        ? object.paymentShippingInfos.map((e: any) => PaymentShippingInfo.fromJSON(e))
        : [],
      paymentShippingOverseaInfos: Array.isArray(object?.paymentShippingOverseaInfos)
        ? object.paymentShippingOverseaInfos.map((e: any) => PaymentShippingOverseaInfo.fromJSON(e))
        : [],
      lastSelectedType: isSet(object.lastSelectedType) ? salesOrder_PaymentTypeFromJSON(object.lastSelectedType) : 0,
      lastOverseaName: isSet(object.lastOverseaName) ? String(object.lastOverseaName) : "",
      exchangeRateUsdNtd: isSet(object.exchangeRateUsdNtd) ? String(object.exchangeRateUsdNtd) : "",
      acquiringBank: isSet(object.acquiringBank) ? payment_AcquiringBankFromJSON(object.acquiringBank) : 0,
    };
  },

  toJSON(message: GetShippingInfosResponse_ShippingInfo): unknown {
    const obj: any = {};
    if (message.paymentShippingInfos) {
      obj.paymentShippingInfos = message.paymentShippingInfos.map((e) => e ? PaymentShippingInfo.toJSON(e) : undefined);
    } else {
      obj.paymentShippingInfos = [];
    }
    if (message.paymentShippingOverseaInfos) {
      obj.paymentShippingOverseaInfos = message.paymentShippingOverseaInfos.map((e) =>
        e ? PaymentShippingOverseaInfo.toJSON(e) : undefined
      );
    } else {
      obj.paymentShippingOverseaInfos = [];
    }
    message.lastSelectedType !== undefined &&
      (obj.lastSelectedType = salesOrder_PaymentTypeToJSON(message.lastSelectedType));
    message.lastOverseaName !== undefined && (obj.lastOverseaName = message.lastOverseaName);
    message.exchangeRateUsdNtd !== undefined && (obj.exchangeRateUsdNtd = message.exchangeRateUsdNtd);
    message.acquiringBank !== undefined && (obj.acquiringBank = payment_AcquiringBankToJSON(message.acquiringBank));
    return obj;
  },

  create<I extends Exact<DeepPartial<GetShippingInfosResponse_ShippingInfo>, I>>(
    base?: I,
  ): GetShippingInfosResponse_ShippingInfo {
    return GetShippingInfosResponse_ShippingInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetShippingInfosResponse_ShippingInfo>, I>>(
    object: I,
  ): GetShippingInfosResponse_ShippingInfo {
    const message = createBaseGetShippingInfosResponse_ShippingInfo();
    message.paymentShippingInfos = object.paymentShippingInfos?.map((e) => PaymentShippingInfo.fromPartial(e)) || [];
    message.paymentShippingOverseaInfos =
      object.paymentShippingOverseaInfos?.map((e) => PaymentShippingOverseaInfo.fromPartial(e)) || [];
    message.lastSelectedType = object.lastSelectedType ?? 0;
    message.lastOverseaName = object.lastOverseaName ?? "";
    message.exchangeRateUsdNtd = object.exchangeRateUsdNtd ?? "";
    message.acquiringBank = object.acquiringBank ?? 0;
    return message;
  },
};

function createBaseGetShippingInfosResponse_CartInfo(): GetShippingInfosResponse_CartInfo {
  return { originPriceTotal: "", promotionDeduction: "", balance: "" };
}

export const GetShippingInfosResponse_CartInfo = {
  fromJSON(object: any): GetShippingInfosResponse_CartInfo {
    return {
      originPriceTotal: isSet(object.originPriceTotal) ? String(object.originPriceTotal) : "",
      promotionDeduction: isSet(object.promotionDeduction) ? String(object.promotionDeduction) : "",
      balance: isSet(object.balance) ? String(object.balance) : "",
    };
  },

  toJSON(message: GetShippingInfosResponse_CartInfo): unknown {
    const obj: any = {};
    message.originPriceTotal !== undefined && (obj.originPriceTotal = message.originPriceTotal);
    message.promotionDeduction !== undefined && (obj.promotionDeduction = message.promotionDeduction);
    message.balance !== undefined && (obj.balance = message.balance);
    return obj;
  },

  create<I extends Exact<DeepPartial<GetShippingInfosResponse_CartInfo>, I>>(
    base?: I,
  ): GetShippingInfosResponse_CartInfo {
    return GetShippingInfosResponse_CartInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetShippingInfosResponse_CartInfo>, I>>(
    object: I,
  ): GetShippingInfosResponse_CartInfo {
    const message = createBaseGetShippingInfosResponse_CartInfo();
    message.originPriceTotal = object.originPriceTotal ?? "";
    message.promotionDeduction = object.promotionDeduction ?? "";
    message.balance = object.balance ?? "";
    return message;
  },
};

function createBaseGetShippingInfosResponse_RecipientInfo(): GetShippingInfosResponse_RecipientInfo {
  return { recipientInfos: [], islandPostOfficeNote: "", islandCodNote: "" };
}

export const GetShippingInfosResponse_RecipientInfo = {
  fromJSON(object: any): GetShippingInfosResponse_RecipientInfo {
    return {
      recipientInfos: Array.isArray(object?.recipientInfos)
        ? object.recipientInfos.map((e: any) => RecipientAddress.fromJSON(e))
        : [],
      islandPostOfficeNote: isSet(object.islandPostOfficeNote) ? String(object.islandPostOfficeNote) : "",
      islandCodNote: isSet(object.islandCodNote) ? String(object.islandCodNote) : "",
    };
  },

  toJSON(message: GetShippingInfosResponse_RecipientInfo): unknown {
    const obj: any = {};
    if (message.recipientInfos) {
      obj.recipientInfos = message.recipientInfos.map((e) => e ? RecipientAddress.toJSON(e) : undefined);
    } else {
      obj.recipientInfos = [];
    }
    message.islandPostOfficeNote !== undefined && (obj.islandPostOfficeNote = message.islandPostOfficeNote);
    message.islandCodNote !== undefined && (obj.islandCodNote = message.islandCodNote);
    return obj;
  },

  create<I extends Exact<DeepPartial<GetShippingInfosResponse_RecipientInfo>, I>>(
    base?: I,
  ): GetShippingInfosResponse_RecipientInfo {
    return GetShippingInfosResponse_RecipientInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetShippingInfosResponse_RecipientInfo>, I>>(
    object: I,
  ): GetShippingInfosResponse_RecipientInfo {
    const message = createBaseGetShippingInfosResponse_RecipientInfo();
    message.recipientInfos = object.recipientInfos?.map((e) => RecipientAddress.fromPartial(e)) || [];
    message.islandPostOfficeNote = object.islandPostOfficeNote ?? "";
    message.islandCodNote = object.islandCodNote ?? "";
    return message;
  },
};

function createBaseGetShippingInfosResponse_ReceiptInfo(): GetShippingInfosResponse_ReceiptInfo {
  return { receiptMethod: 0, mobileBarcode: "", taxId: "", donateUnitId: "", donations: [] };
}

export const GetShippingInfosResponse_ReceiptInfo = {
  fromJSON(object: any): GetShippingInfosResponse_ReceiptInfo {
    return {
      receiptMethod: isSet(object.receiptMethod) ? salesOrder_ReceiptMethodFromJSON(object.receiptMethod) : 0,
      mobileBarcode: isSet(object.mobileBarcode) ? String(object.mobileBarcode) : "",
      taxId: isSet(object.taxId) ? String(object.taxId) : "",
      donateUnitId: isSet(object.donateUnitId) ? String(object.donateUnitId) : "",
      donations: Array.isArray(object?.donations)
        ? object.donations.map((e: any) => Receipt_DonationUnit.fromJSON(e))
        : [],
    };
  },

  toJSON(message: GetShippingInfosResponse_ReceiptInfo): unknown {
    const obj: any = {};
    message.receiptMethod !== undefined && (obj.receiptMethod = salesOrder_ReceiptMethodToJSON(message.receiptMethod));
    message.mobileBarcode !== undefined && (obj.mobileBarcode = message.mobileBarcode);
    message.taxId !== undefined && (obj.taxId = message.taxId);
    message.donateUnitId !== undefined && (obj.donateUnitId = message.donateUnitId);
    if (message.donations) {
      obj.donations = message.donations.map((e) => e ? Receipt_DonationUnit.toJSON(e) : undefined);
    } else {
      obj.donations = [];
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<GetShippingInfosResponse_ReceiptInfo>, I>>(
    base?: I,
  ): GetShippingInfosResponse_ReceiptInfo {
    return GetShippingInfosResponse_ReceiptInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<GetShippingInfosResponse_ReceiptInfo>, I>>(
    object: I,
  ): GetShippingInfosResponse_ReceiptInfo {
    const message = createBaseGetShippingInfosResponse_ReceiptInfo();
    message.receiptMethod = object.receiptMethod ?? 0;
    message.mobileBarcode = object.mobileBarcode ?? "";
    message.taxId = object.taxId ?? "";
    message.donateUnitId = object.donateUnitId ?? "";
    message.donations = object.donations?.map((e) => Receipt_DonationUnit.fromPartial(e)) || [];
    return message;
  },
};

function createBaseVerifyReceiptBarcodeRequest(): VerifyReceiptBarcodeRequest {
  return { barcode: "" };
}

export const VerifyReceiptBarcodeRequest = {
  fromJSON(object: any): VerifyReceiptBarcodeRequest {
    return { barcode: isSet(object.barcode) ? String(object.barcode) : "" };
  },

  toJSON(message: VerifyReceiptBarcodeRequest): unknown {
    const obj: any = {};
    message.barcode !== undefined && (obj.barcode = message.barcode);
    return obj;
  },

  create<I extends Exact<DeepPartial<VerifyReceiptBarcodeRequest>, I>>(base?: I): VerifyReceiptBarcodeRequest {
    return VerifyReceiptBarcodeRequest.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<VerifyReceiptBarcodeRequest>, I>>(object: I): VerifyReceiptBarcodeRequest {
    const message = createBaseVerifyReceiptBarcodeRequest();
    message.barcode = object.barcode ?? "";
    return message;
  },
};

function createBaseCart(): Cart {
  return { items: [], promotions: [], announcement: undefined, balance: "", remindProhibitOverseaSale: "" };
}

export const Cart = {
  fromJSON(object: any): Cart {
    return {
      items: Array.isArray(object?.items) ? object.items.map((e: any) => Cart_Item.fromJSON(e)) : [],
      promotions: Array.isArray(object?.promotions)
        ? object.promotions.map((e: any) => Cart_PromotionInfo.fromJSON(e))
        : [],
      announcement: isSet(object.announcement) ? Announcement.fromJSON(object.announcement) : undefined,
      balance: isSet(object.balance) ? String(object.balance) : "",
      remindProhibitOverseaSale: isSet(object.remindProhibitOverseaSale)
        ? String(object.remindProhibitOverseaSale)
        : "",
    };
  },

  toJSON(message: Cart): unknown {
    const obj: any = {};
    if (message.items) {
      obj.items = message.items.map((e) => e ? Cart_Item.toJSON(e) : undefined);
    } else {
      obj.items = [];
    }
    if (message.promotions) {
      obj.promotions = message.promotions.map((e) => e ? Cart_PromotionInfo.toJSON(e) : undefined);
    } else {
      obj.promotions = [];
    }
    message.announcement !== undefined &&
      (obj.announcement = message.announcement ? Announcement.toJSON(message.announcement) : undefined);
    message.balance !== undefined && (obj.balance = message.balance);
    message.remindProhibitOverseaSale !== undefined &&
      (obj.remindProhibitOverseaSale = message.remindProhibitOverseaSale);
    return obj;
  },

  create<I extends Exact<DeepPartial<Cart>, I>>(base?: I): Cart {
    return Cart.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Cart>, I>>(object: I): Cart {
    const message = createBaseCart();
    message.items = object.items?.map((e) => Cart_Item.fromPartial(e)) || [];
    message.promotions = object.promotions?.map((e) => Cart_PromotionInfo.fromPartial(e)) || [];
    message.announcement = (object.announcement !== undefined && object.announcement !== null)
      ? Announcement.fromPartial(object.announcement)
      : undefined;
    message.balance = object.balance ?? "";
    message.remindProhibitOverseaSale = object.remindProhibitOverseaSale ?? "";
    return message;
  },
};

function createBaseCart_Item(): Cart_Item {
  return {
    skuId: "",
    quantity: 0,
    selected: false,
    meta: undefined,
    published: false,
    image: "",
    colorName: "",
    sizeName: "",
    promotionId: "",
    inventory: 0,
    boughtBefore: false,
    prohibitOverseaSale: false,
    sizeSuffix: "",
  };
}

export const Cart_Item = {
  fromJSON(object: any): Cart_Item {
    return {
      skuId: isSet(object.skuId) ? String(object.skuId) : "",
      quantity: isSet(object.quantity) ? Number(object.quantity) : 0,
      selected: isSet(object.selected) ? Boolean(object.selected) : false,
      meta: isSet(object.meta) ? ProductMeta.fromJSON(object.meta) : undefined,
      published: isSet(object.published) ? Boolean(object.published) : false,
      image: isSet(object.image) ? String(object.image) : "",
      colorName: isSet(object.colorName) ? String(object.colorName) : "",
      sizeName: isSet(object.sizeName) ? String(object.sizeName) : "",
      promotionId: isSet(object.promotionId) ? String(object.promotionId) : "",
      inventory: isSet(object.inventory) ? Number(object.inventory) : 0,
      boughtBefore: isSet(object.boughtBefore) ? Boolean(object.boughtBefore) : false,
      prohibitOverseaSale: isSet(object.prohibitOverseaSale) ? Boolean(object.prohibitOverseaSale) : false,
      sizeSuffix: isSet(object.sizeSuffix) ? String(object.sizeSuffix) : "",
    };
  },

  toJSON(message: Cart_Item): unknown {
    const obj: any = {};
    message.skuId !== undefined && (obj.skuId = message.skuId);
    message.quantity !== undefined && (obj.quantity = Math.round(message.quantity));
    message.selected !== undefined && (obj.selected = message.selected);
    message.meta !== undefined && (obj.meta = message.meta ? ProductMeta.toJSON(message.meta) : undefined);
    message.published !== undefined && (obj.published = message.published);
    message.image !== undefined && (obj.image = message.image);
    message.colorName !== undefined && (obj.colorName = message.colorName);
    message.sizeName !== undefined && (obj.sizeName = message.sizeName);
    message.promotionId !== undefined && (obj.promotionId = message.promotionId);
    message.inventory !== undefined && (obj.inventory = Math.round(message.inventory));
    message.boughtBefore !== undefined && (obj.boughtBefore = message.boughtBefore);
    message.prohibitOverseaSale !== undefined && (obj.prohibitOverseaSale = message.prohibitOverseaSale);
    message.sizeSuffix !== undefined && (obj.sizeSuffix = message.sizeSuffix);
    return obj;
  },

  create<I extends Exact<DeepPartial<Cart_Item>, I>>(base?: I): Cart_Item {
    return Cart_Item.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Cart_Item>, I>>(object: I): Cart_Item {
    const message = createBaseCart_Item();
    message.skuId = object.skuId ?? "";
    message.quantity = object.quantity ?? 0;
    message.selected = object.selected ?? false;
    message.meta = (object.meta !== undefined && object.meta !== null)
      ? ProductMeta.fromPartial(object.meta)
      : undefined;
    message.published = object.published ?? false;
    message.image = object.image ?? "";
    message.colorName = object.colorName ?? "";
    message.sizeName = object.sizeName ?? "";
    message.promotionId = object.promotionId ?? "";
    message.inventory = object.inventory ?? 0;
    message.boughtBefore = object.boughtBefore ?? false;
    message.prohibitOverseaSale = object.prohibitOverseaSale ?? false;
    message.sizeSuffix = object.sizeSuffix ?? "";
    return message;
  },
};

function createBaseCart_PromotionInfo(): Cart_PromotionInfo {
  return { id: "", name: "", enabled: false, discountType: 0, discount: undefined };
}

export const Cart_PromotionInfo = {
  fromJSON(object: any): Cart_PromotionInfo {
    return {
      id: isSet(object.id) ? String(object.id) : "",
      name: isSet(object.name) ? String(object.name) : "",
      enabled: isSet(object.enabled) ? Boolean(object.enabled) : false,
      discountType: isSet(object.discountType) ? cart_PromotionInfo_DiscountTypeFromJSON(object.discountType) : 0,
      discount: isSet(object.discount) ? Cart_PromotionInfo_Discount.fromJSON(object.discount) : undefined,
    };
  },

  toJSON(message: Cart_PromotionInfo): unknown {
    const obj: any = {};
    message.id !== undefined && (obj.id = message.id);
    message.name !== undefined && (obj.name = message.name);
    message.enabled !== undefined && (obj.enabled = message.enabled);
    message.discountType !== undefined &&
      (obj.discountType = cart_PromotionInfo_DiscountTypeToJSON(message.discountType));
    message.discount !== undefined &&
      (obj.discount = message.discount ? Cart_PromotionInfo_Discount.toJSON(message.discount) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<Cart_PromotionInfo>, I>>(base?: I): Cart_PromotionInfo {
    return Cart_PromotionInfo.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Cart_PromotionInfo>, I>>(object: I): Cart_PromotionInfo {
    const message = createBaseCart_PromotionInfo();
    message.id = object.id ?? "";
    message.name = object.name ?? "";
    message.enabled = object.enabled ?? false;
    message.discountType = object.discountType ?? 0;
    message.discount = (object.discount !== undefined && object.discount !== null)
      ? Cart_PromotionInfo_Discount.fromPartial(object.discount)
      : undefined;
    return message;
  },
};

function createBaseCart_PromotionInfo_Discount(): Cart_PromotionInfo_Discount {
  return { itemNum: undefined, gift: undefined };
}

export const Cart_PromotionInfo_Discount = {
  fromJSON(object: any): Cart_PromotionInfo_Discount {
    return {
      itemNum: isSet(object.itemNum) ? Number(object.itemNum) : undefined,
      gift: isSet(object.gift) ? Cart_PromotionInfo_Discount_Gift.fromJSON(object.gift) : undefined,
    };
  },

  toJSON(message: Cart_PromotionInfo_Discount): unknown {
    const obj: any = {};
    message.itemNum !== undefined && (obj.itemNum = Math.round(message.itemNum));
    message.gift !== undefined &&
      (obj.gift = message.gift ? Cart_PromotionInfo_Discount_Gift.toJSON(message.gift) : undefined);
    return obj;
  },

  create<I extends Exact<DeepPartial<Cart_PromotionInfo_Discount>, I>>(base?: I): Cart_PromotionInfo_Discount {
    return Cart_PromotionInfo_Discount.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Cart_PromotionInfo_Discount>, I>>(object: I): Cart_PromotionInfo_Discount {
    const message = createBaseCart_PromotionInfo_Discount();
    message.itemNum = object.itemNum ?? undefined;
    message.gift = (object.gift !== undefined && object.gift !== null)
      ? Cart_PromotionInfo_Discount_Gift.fromPartial(object.gift)
      : undefined;
    return message;
  },
};

function createBaseCart_PromotionInfo_Discount_Gift(): Cart_PromotionInfo_Discount_Gift {
  return { price: "", productId: "" };
}

export const Cart_PromotionInfo_Discount_Gift = {
  fromJSON(object: any): Cart_PromotionInfo_Discount_Gift {
    return {
      price: isSet(object.price) ? String(object.price) : "",
      productId: isSet(object.productId) ? String(object.productId) : "",
    };
  },

  toJSON(message: Cart_PromotionInfo_Discount_Gift): unknown {
    const obj: any = {};
    message.price !== undefined && (obj.price = message.price);
    message.productId !== undefined && (obj.productId = message.productId);
    return obj;
  },

  create<I extends Exact<DeepPartial<Cart_PromotionInfo_Discount_Gift>, I>>(
    base?: I,
  ): Cart_PromotionInfo_Discount_Gift {
    return Cart_PromotionInfo_Discount_Gift.fromPartial(base ?? {});
  },

  fromPartial<I extends Exact<DeepPartial<Cart_PromotionInfo_Discount_Gift>, I>>(
    object: I,
  ): Cart_PromotionInfo_Discount_Gift {
    const message = createBaseCart_PromotionInfo_Discount_Gift();
    message.price = object.price ?? "";
    message.productId = object.productId ?? "";
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var tsProtoGlobalThis: any = (() => {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
